import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TreeNode } from 'primeng/api';
import { TreeModule } from 'primeng/tree';
import { ButtonModule } from 'primeng/button';
import { TranslatePipe } from '@ngx-translate/core';
import { finalize, forkJoin } from 'rxjs';
import { BetriebsVariableNode } from '@entity/chop';
import { AnwenderbetriebService } from '@service/anwenderbetrieb.service';

@Component({
  selector: 'app-betriebsvariable-picker-dialog',
  templateUrl: './betriebsvariable-picker-dialog.component.html',
  styleUrls: ['./betriebsvariable-picker-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TreeModule, ButtonModule, TranslatePipe],
})
export class BetriebsvariablePickerDialogComponent {
  private dialogConfig = inject(DynamicDialogConfig);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private dialogRef = inject(DynamicDialogRef);
  private anwenderbetriebService = inject(AnwenderbetriebService);
  private destroyRef = inject(DestroyRef);

  private anwenderbetriebId: number = this.dialogConfig.data.anwenderbetriebId;
  private profilId: number = this.dialogConfig.data.profilId;
  private alreadyAssignedIds = new Set<number>(this.dialogConfig.data.alreadyAssignedIds ?? []);

  treeNodes: TreeNode[] = [];
  selection: TreeNode[] = [];
  submitting = false;
  loaded = false;

  constructor() {
    this.loadBetriebsVariablen();
  }

  onSubmit(): void {
    const selectedIds = this.selection
      .map((n) => n.data as BetriebsVariableNode | undefined)
      .filter((d): d is BetriebsVariableNode => !!d && d.isBetriebsVariable && !this.alreadyAssignedIds.has(d.id))
      .map((d) => d.id);
    if (selectedIds.length === 0) {
      return;
    }
    this.submitting = true;
    forkJoin(
      selectedIds.map((id) =>
        this.anwenderbetriebService.addChopProfilVariable(this.anwenderbetriebId, this.profilId, id)
      )
    )
      .pipe(
        finalize(() => (this.submitting = false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (results) => this.dialogRef.close(results),
        error: () => (this.submitting = false),
      });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private loadBetriebsVariablen(): void {
    this.anwenderbetriebService
      .getChopBetriebsVariablen(this.anwenderbetriebId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((nodes) => {
        this.treeNodes = this.buildTree(nodes);
        this.loaded = true;
        this.changeDetectorRef.markForCheck();
      });
  }

  private buildTree(nodes: BetriebsVariableNode[]): TreeNode[] {
    const treeNodes = new Map<number, TreeNode>();

    for (const node of nodes) {
      treeNodes.set(node.id, {
        key: String(node.id),
        label: `${node.tacsCode} ${node.bezeichnung}`,
        data: node,
        selectable: node.isBetriebsVariable && !this.alreadyAssignedIds.has(node.id),
        children: [],
        expanded: !node.isBetriebsVariable,
      });
    }

    const roots: TreeNode[] = [];
    for (const node of nodes) {
      const treeNode = treeNodes.get(node.id);
      if (!treeNode) {
        continue;
      }
      const parentTreeNode = node.supraId != null ? treeNodes.get(node.supraId) : undefined;
      if (parentTreeNode) {
        parentTreeNode.children!.push(treeNode);
      } else {
        roots.push(treeNode);
      }
    }

    return this.pruneEmptyBranches(roots);
  }

  private pruneEmptyBranches(nodes: TreeNode[]): TreeNode[] {
    return nodes
      .map((n) => ({ ...n, children: this.pruneEmptyBranches(n.children ?? []) }))
      .filter((n) => (n.data as BetriebsVariableNode).isBetriebsVariable || (n.children?.length ?? 0) > 0);
  }
}
